import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchHeatmap, fetchClientBlockers, fetchClients, fetchWeeks } from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";
import CreateTaskFromBlockerButton from "../components/tasks/CreateTaskFromBlockerButton";
import { formatWeekDate } from "../utils/formatWeek";
import {
  HEATMAP_GRAINS,
  formatPeriodLabel,
  periodKeyFromWeek,
  periodsFromWeeks,
  snapPeriod,
} from "../utils/heatmapPeriods";

// Цвет по уровню нагрузки. CSS-классы: low=красный, medium=жёлтый, high=зелёный.
// Для нагрузки логика обратная оценке качества: высокая нагрузка (перегруз) — красная,
// низкая (свободно) — зелёная.
function getScoreClass(v) {
  if (v == null || Number.isNaN(Number(v))) return "neutral";
  const n = Number(v);
  if (n < 2.5) return "high";
  if (n < 3.5) return "medium";
  return "low";
}

function getInitials(name) {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLORS = [
  "#2f9e9b",
  "#4f6f8f",
  "#8f5f73",
  "#6a8caf",
  "#5aa469",
  "#b9786f",
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function pairKey(clientId, employeeId) {
  return `${clientId}:${employeeId}`;
}

function HeatmapLegend() {
  const items = [
    { tone: "high", label: "1–2 · низкая" },
    { tone: "medium", label: "3 · средняя" },
    { tone: "low", label: "4–5 · высокая" },
  ];

  return (
    <div className="heatmap-legend">
      <span className="heatmap-legend-label">Легенда нагрузки:</span>
      {items.map((item) => (
        <div key={item.tone} className="heatmap-legend-item">
          <span className={`heatmap-mark ${item.tone}`} aria-hidden="true" />
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function HeatmapStats({ filledCount, clientCount, employeeCount, scores }) {
  const avgScore =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : null;

  const lowCount = scores.filter((s) => getScoreClass(s) === "high").length;
  const mediumCount = scores.filter((s) => getScoreClass(s) === "medium").length;
  const highCount = scores.filter((s) => getScoreClass(s) === "low").length;

  return (
    <div className="stats-bar heatmap-stats">
      <div className="stat-card">
        <span className="stat-value">{clientCount}</span>
        <span className="stat-label">Клиентов с оценками</span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{employeeCount}</span>
        <span className="stat-label">Сотрудников с оценками</span>
      </div>
      <div className="stat-card">
        <span className="stat-value">{filledCount}</span>
        <span className="stat-label">Оценок в матрице</span>
      </div>
      <div className="stat-card">
        <span
          className="stat-value"
          style={{
            color: `var(--score-${getScoreClass(avgScore == null ? null : Number(avgScore))})`,
          }}
        >
          {avgScore || "—"}
        </span>
        <span className="stat-label">Средняя нагрузка</span>
      </div>
      <div className="stat-card">
        <div className="heatmap-dist">
          <div className="heatmap-dist-item" style={{ color: "var(--score-high)" }}>
            <span className="heatmap-dist-num">{lowCount}</span>
            <span className="heatmap-dist-label">низк</span>
          </div>
          <div className="heatmap-dist-sep" />
          <div className="heatmap-dist-item" style={{ color: "var(--score-medium)" }}>
            <span className="heatmap-dist-num">{mediumCount}</span>
            <span className="heatmap-dist-label">сред</span>
          </div>
          <div className="heatmap-dist-sep" />
          <div className="heatmap-dist-item" style={{ color: "var(--score-low)" }}>
            <span className="heatmap-dist-num">{highCount}</span>
            <span className="heatmap-dist-label">выс</span>
          </div>
        </div>
        <span className="stat-label">Распределение оценок</span>
      </div>
    </div>
  );
}

function WorkloadReasonsTable({ reasons, grain }) {
  const showWeek = grain !== "week";
  const rows = useMemo(() => {
    return [...reasons].sort((a, b) => {
      const scoreA = a.score_workload == null ? -1 : a.score_workload;
      const scoreB = b.score_workload == null ? -1 : b.score_workload;
      if (scoreB !== scoreA) return scoreB - scoreA;
      const emp = (a.employee_name || "").localeCompare(b.employee_name || "", "ru");
      if (emp !== 0) return emp;
      const client = (a.client_name || "").localeCompare(b.client_name || "", "ru");
      if (client !== 0) return client;
      return String(b.week_start || "").localeCompare(String(a.week_start || ""));
    });
  }, [reasons]);

  return (
    <div className="table-container">
      <table className="score-table team-week-workload-table">
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Клиент</th>
            {showWeek && <th>Неделя</th>}
            <th>Балл</th>
            <th>Причина</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr
              key={`wr-${w.client_id}-${w.employee_id}-${w.week_start}-${w.score_workload}-${w.workload_comment}`}
            >
              <td className="team-week-workload-emp">{w.employee_name}</td>
              <td>{w.client_name}</td>
              {showWeek && <td>{formatWeekDate(w.week_start)}</td>}
              <td>
                <span className={`score-badge ${getScoreClass(w.score_workload)}`}>
                  {w.score_workload ?? "—"}
                </span>
              </td>
              <td className="cell-text">{w.workload_comment}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ClientHeatmap() {
  const [weeks, setWeeks] = useState([]);
  const [grain, setGrain] = useState("week");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [heatmap, setHeatmap] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);
  const [dataError, setDataError] = useState(null);
  const requestIdRef = useRef(0);

  const loadMeta = useCallback(() => {
    setMetaError(null);
    Promise.all([fetchWeeks(), fetchClients()])
      .then(([weeksData, clientsData]) => {
        setWeeks(weeksData);
        setClients(clientsData);
        if (weeksData.length > 0) {
          setSelectedPeriod((prev) => prev || periodKeyFromWeek(grain, weeksData[0]));
        }
      })
      .catch((err) => {
        console.error("Failed to load heatmap meta:", err);
        setMetaError("Не удалось загрузить списки недель и клиентов");
      });
  }, []);

  const loadHeatmap = useCallback(() => {
    if (!selectedPeriod) return;
    setDataError(null);
    setLoading(true);
    const requestId = ++requestIdRef.current;
    Promise.all([
      fetchHeatmap({ grain, period: selectedPeriod, clientId: selectedClient || undefined }),
      fetchClientBlockers({ grain, period: selectedPeriod, clientId: selectedClient || undefined }),
    ])
      .then(([heat, block]) => {
        if (requestId !== requestIdRef.current) return;
        setHeatmap(heat);
        setBlockers(block);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load heatmap:", err);
        setDataError("Не удалось загрузить данные тепловой карты");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [grain, selectedPeriod, selectedClient]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadHeatmap();
  }, [loadHeatmap]);

  const {
    cellMap,
    clientRows,
    employeeCols,
    scores,
    workloadReasons,
  } = useMemo(() => {
    const map = new Map();
    const clientMeta = new Map();
    const employeeMeta = new Map();
    const scoreList = [];
    const reasons = [];

    for (const h of heatmap) {
      if (h.score_workload == null) continue;
      map.set(pairKey(h.client_id, h.employee_id), h);
      scoreList.push(h.score_workload);

      if (!clientMeta.has(h.client_id)) {
        clientMeta.set(h.client_id, { id: h.client_id, name: h.client_name, sum: 0, n: 0 });
      }
      const c = clientMeta.get(h.client_id);
      c.sum += h.score_workload;
      c.n += 1;

      if (!employeeMeta.has(h.employee_id)) {
        employeeMeta.set(h.employee_id, {
          id: h.employee_id,
          name: h.employee_name,
        });
      }

      for (const comment of h.comments || []) {
        if (comment.score >= 4 && comment.text) {
          reasons.push({
            client_id: h.client_id,
            client_name: h.client_name,
            employee_id: h.employee_id,
            employee_name: h.employee_name,
            score_workload: comment.score,
            workload_comment: comment.text,
            week_start: comment.week_start,
          });
        }
      }
    }

    const clientsSorted = [...clientMeta.values()].sort((a, b) => {
      const avgA = a.n ? a.sum / a.n : 0;
      const avgB = b.n ? b.sum / b.n : 0;
      if (avgB !== avgA) return avgB - avgA;
      return a.name.localeCompare(b.name, "ru");
    });

    const employeesSorted = [...employeeMeta.values()].sort((a, b) =>
      a.name.localeCompare(b.name, "ru")
    );

    return {
      cellMap: map,
      clientRows: clientsSorted,
      employeeCols: employeesSorted,
      scores: scoreList,
      workloadReasons: reasons,
    };
  }, [heatmap]);

  const getCell = (clientId, employeeId) =>
    cellMap.get(pairKey(clientId, employeeId)) || null;

  const clientAvg = (client) => {
    if (!client.n) return null;
    return (client.sum / client.n).toFixed(1);
  };

  const periods = useMemo(() => periodsFromWeeks(grain, weeks), [grain, weeks]);
  const periodIndex = periods.indexOf(selectedPeriod);
  const goPeriod = (delta) => {
    if (periodIndex < 0) return;
    const next = periods[periodIndex + delta];
    if (next) setSelectedPeriod(next);
  };

  const changeGrain = (next) => {
    setGrain(next);
    setSelectedPeriod(snapPeriod(next, grain, selectedPeriod, weeks));
  };

  const grainMeta = HEATMAP_GRAINS.find((item) => item.value === grain) || HEATMAP_GRAINS[0];

  if (metaError) {
    return <ErrorRetry message={metaError} onRetry={loadMeta} />;
  }

  if (loading && heatmap.length === 0 && !dataError) {
    return <Loading text="Загрузка тепловой карты..." />;
  }

  const hasData = scores.length > 0;
  const isRefreshing = loading && heatmap.length > 0;

  return (
    <div
      className={`client-heatmap animate-fade-in${
        isRefreshing ? " is-refreshing" : ""
      }`}
    >
      <div className="section-header">
        <div>
          <h2>Загрузка по клиентам</h2>
          <div className="section-subtitle">
            Матрица «клиент × сотрудник» по нагрузке за {grainMeta.prepositional}
          </div>
        </div>
        <div className="controls">
          {isRefreshing && (
            <span className="heatmap-refresh-hint" aria-live="polite">
              Обновление…
            </span>
          )}
          <div className="toolbar-segment" role="group" aria-label="Период">
            {HEATMAP_GRAINS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={grain === item.value ? "active" : ""}
                aria-pressed={grain === item.value}
                onClick={() => changeGrain(item.value)}
                disabled={isRefreshing}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => goPeriod(1)}
            disabled={periodIndex < 0 || periodIndex >= periods.length - 1 || isRefreshing}
            title="Более ранний период"
          >
            ←
          </button>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={isRefreshing}
          >
            {periods.map((period) => (
              <option key={period} value={period}>
                {formatPeriodLabel(grain, period)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => goPeriod(-1)}
            disabled={periodIndex <= 0 || isRefreshing}
            title="Более поздний период"
          >
            →
          </button>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            disabled={isRefreshing}
          >
            <option value="">Все клиенты</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {dataError && <ErrorRetry message={dataError} onRetry={loadHeatmap} />}

      {hasData ? (
        <>
          <HeatmapStats
            filledCount={scores.length}
            clientCount={clientRows.length}
            employeeCount={employeeCols.length}
            scores={scores}
          />
          <HeatmapLegend />
        </>
      ) : (
        !dataError && (
          <div className="empty-state">
            Нет данных о загрузке по клиентам за выбранный период
          </div>
        )
      )}

      {hasData && (
        <div className="data-card">
          <div className="data-card-header">
            <div className="data-card-title">Матрица загрузки</div>
            <span className="heatmap-matrix-caption">
              {scores.length} оценок · {clientRows.length} клиентов ·{" "}
              {employeeCols.length} сотрудников
            </span>
          </div>
          <div className="data-card-body" style={{ padding: 0 }}>
            <div className="table-scroll">
              <table className="heatmap-table">
                <thead>
                  <tr>
                    <th>Клиент</th>
                    {employeeCols.map((emp) => (
                      <th key={emp.id} className="heatmap-col">
                        <div className="heatmap-emp-head">
                          <div
                            className="heatmap-emp-avatar"
                            style={{ background: getAvatarColor(emp.name) }}
                            title={emp.name}
                          >
                            {getInitials(emp.name)}
                          </div>
                          <span className="heatmap-emp-name">
                            {emp.name.split(" ")[0]}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className="heatmap-col">Среднее</th>
                  </tr>
                </thead>
                <tbody>
                  {clientRows.map((client) => {
                    const avg = clientAvg(client);
                    return (
                      <tr key={client.id}>
                        <td>
                          <div className="employee-cell">
                            <div
                              className="employee-avatar"
                              style={{ background: getAvatarColor(client.name) }}
                            >
                              {getInitials(client.name)}
                            </div>
                            <span className="employee-name">{client.name}</span>
                          </div>
                        </td>
                        {employeeCols.map((emp) => {
                          const cell = getCell(client.id, emp.id);
                          const score = cell?.score_workload ?? null;
                          const comments = cell?.comments || [];
                          const displayScore =
                            score == null
                              ? null
                              : grain === "week"
                                ? score
                                : Number(score).toFixed(1);
                          const title =
                            score != null
                              ? [
                                  `${client.name} → ${emp.name}: ${displayScore}`,
                                  ...comments.map(
                                    (c) => `${formatWeekDate(c.week_start)}: ${c.text}`,
                                  ),
                                ].join("\n")
                              : "Нет оценки";
                          return (
                            <td
                              key={`${client.id}-${emp.id}`}
                              className="heatmap-cell"
                              title={title}
                            >
                              {score != null ? (
                                <span className={`heatmap-mark ${getScoreClass(score)}`}>
                                  {displayScore}
                                  {comments.length > 0 ? (
                                    <sup className="heatmap-note-mark">✎</sup>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="heatmap-empty">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="heatmap-avg-cell">
                          <span className={`score-badge ${getScoreClass(avg == null ? null : Number(avg))}`}>
                            {avg || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {workloadReasons.length > 0 && (
        <div className="data-card heatmap-followup-card">
          <div className="data-card-header">
            <div className="data-card-title heatmap-title-warn">
              Причины высокой нагрузки
            </div>
            <span className="heatmap-matrix-caption">{workloadReasons.length}</span>
          </div>
          <div className="data-card-body">
            <WorkloadReasonsTable reasons={workloadReasons} grain={grain} />
          </div>
        </div>
      )}

      {blockers.length > 0 && (
        <div className="data-card heatmap-followup-card">
          <div className="data-card-header">
            <div className="data-card-title heatmap-title-warn">
              Блокеры по клиентам
            </div>
            <span className="heatmap-matrix-caption">{blockers.length}</span>
          </div>
          <div className="data-card-body">
            <div className="blockers-grid">
              {blockers.map((b) => (
                <div
                  key={`cb-${b.client_response_id}`}
                  className="blocker-card"
                >
                  <div className="blocker-header">
                    <span className="blocker-author">
                      {b.employee_name} → {b.client_name}
                    </span>
                    <CreateTaskFromBlockerButton
                      sourceResponseId={b.response_id}
                      sourceClientResponseId={b.client_response_id}
                    />
                  </div>
                  <div className="blocker-text">{b.blocker_text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
